import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, patch, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { FormDialog } from "@/components/common/FormDialog";
import { SearchInput } from "@/components/common/SearchInput";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Star, Plus, UserCheck } from "lucide-react";
import { toast } from "sonner";

const BORDER = { won: "border-t-emerald-500", lost: "border-t-red-500", standard: "border-t-blue-400" };
const SOURCES = ["referral", "inbound", "outbound", "event"];

export default function Recruitment() {
  const navigate = useNavigate();
  const { can } = useApp();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const { data, isLoading } = useQuery({ queryKey: ["recruitment"], queryFn: () => get("/hrms/recruitment") });

  const move = useMutation({
    mutationFn: ({ cid, stage_id }) => patch(`/hrms/candidates/${cid}`, { stage_id }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["recruitment"] }); toast.success("Candidate moved"); },
  });
  const create = useMutation({
    mutationFn: (body) => post("/hrms/candidates", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["recruitment"] }); toast.success("Candidate added"); },
  });

  const filteredCandidates = useMemo(() => {
    const candidates = data?.candidates || [];
    return candidates.filter((c) => {
      if (search && !`${c.name} ${c.role}`.toLowerCase().includes(search.toLowerCase())) return false;
      if (sourceFilter !== "all" && c.source !== sourceFilter) return false;
      return true;
    });
  }, [data, search, sourceFilter]);

  if (isLoading || !data) return <Skeleton className="h-96" />;

  const onDrop = (e, stage) => {
    const cid = e.dataTransfer.getData("cid");
    if (cid) move.mutate({ cid, stage_id: stage.id });
  };

  return (
    <div>
      <PageHeader title="Recruitment" subtitle="Hiring pipeline across open requisitions.">
        <FormDialog title="New Candidate" testid="new-candidate-modal"
          trigger={<Button data-testid="new-candidate-btn"><Plus className="mr-1.5 h-4 w-4" />New Candidate</Button>}
          fields={[
            { name: "name", label: "Candidate Name", required: true },
            { name: "role", label: "Role", required: true },
            { name: "email", label: "Email", type: "email" },
            { name: "requisition", label: "Requisition" },
            { name: "source", label: "Source", type: "select", options: ["referral", "inbound", "outbound", "event"] },
            { name: "stage_id", label: "Stage", type: "select", options: data.stages.map((s) => ({ value: s.id, label: s.name })) },
          ]}
          onSubmit={(v) => create.mutateAsync(v)} />
      </PageHeader>

      <div className="mb-4 flex items-center gap-3">
        <SearchInput testid="recruit-search" placeholder="Search name or role" value={search} onChange={setSearch} />
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-44" data-testid="recruit-source-filter"><SelectValue placeholder="All sources" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {SOURCES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="recruitment-board">
        {data.stages.map((stage) => {
          const cards = filteredCandidates.filter((c) => c.stage_id === stage.id);
          return (
            <div key={stage.id} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, stage)}
              className="flex w-64 shrink-0 flex-col rounded-xl bg-muted/40">
              <div className={cn("flex items-center justify-between rounded-t-xl border-t-[3px] bg-card px-3 py-2.5", BORDER[stage.type])}>
                <span className="text-[13px] font-semibold">{stage.name}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{cards.length}</span>
              </div>
              <div className="flex flex-col gap-2 p-2">
                {cards.map((c) => (
                  <div key={c.id} data-testid={`cand-${c.id}`} draggable
                    onDragStart={(e) => e.dataTransfer.setData("cid", c.id)}
                    className="cursor-grab rounded-lg border border-border bg-card p-3 hover:shadow-sm">
                    <div className="text-[13px] font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.role}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-[10px] capitalize text-muted-foreground">{c.source}</span>
                      {c.score && <span className="flex items-center gap-0.5 text-xs"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{c.score}</span>}
                    </div>
                    {can("hrms", "create") && (
                      c.converted_employee_id ? (
                        <div className="mt-2 text-[10px] font-medium text-emerald-600">Hired</div>
                      ) : (
                        <Button size="sm" variant="outline" className="mt-2 h-7 w-full text-xs" data-testid={`hire-${c.id}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/hrms/recruitment/${c.id}/hire`); }}>
                          <UserCheck className="mr-1.5 h-3.5 w-3.5" />Hire
                        </Button>
                      )
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
